import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, CircleDot, ArrowRight, CalendarPlus, Video, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { goPro } from '../../lib/checkout';
import { classWhen, fill, joinHref, saveSeat, stepVars } from '../../lib/nextSteps';
import { useClassCountdown } from './Countdown';

const STATUS = {
  complete: { label: 'Complete', Icon: CheckCircle2, cls: 'text-emerald-600' },
  current: { label: 'Up next', Icon: CircleDot, cls: 'text-[#002FA7]' },
  todo: { label: 'Not started', Icon: Circle, cls: 'text-slate-400' },
};

function ClassAction({ cls, texts, onChanged }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  if (!cls) return <span className="text-xs text-slate-500 italic" data-testid="ns-row-no-class">No class scheduled yet — we'll let you know</span>;
  const href = joinHref(cls);
  const t = texts.class_card;
  if (cls.is_locked) {
    return <button type="button" onClick={() => goPro(navigate)} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-2 text-xs font-extrabold transition" data-testid="ns-row-upgrade-btn"><Lock size={13} /> Upgrade to join</button>;
  }
  if (cls.seat_saved) {
    const go = () => (!href ? navigate('/group-classes') : href.startsWith('/') ? navigate(href) : window.open(href, '_blank', 'noopener,noreferrer'));
    return (
      <button type="button" onClick={go} disabled={!cls.join_open} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition ${cls.join_open ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-100 text-slate-500 cursor-not-allowed'}`} data-testid="ns-row-join-btn">
        <Video size={13} /> {cls.join_open ? t.join_label : t.join_soon_label}
      </button>
    );
  }
  const save = async () => {
    setSaving(true);
    try { await saveSeat(cls); toast.success('Seat saved — calendar file downloaded'); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save your seat'); }
    setSaving(false);
  };
  return <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-2 text-xs font-extrabold transition disabled:opacity-60" data-testid="ns-row-save-btn"><CalendarPlus size={13} /> {saving ? 'Saving…' : 'Save my seat'}</button>;
}

function ClassMeta({ cls }) {
  const c = useClassCountdown(cls?.event?.start_at);
  if (!cls) return null;
  return <p className="text-xs text-slate-500 mt-1" data-testid="ns-row-class-meta">{cls.seat_saved ? 'Seat saved · ' : 'Next class · '}{cls.event.title} · {classWhen(cls.event.start_at)}{c && !c.expired ? ` · starts in ${c.short}` : cls.live ? ' · live now' : ''}</p>;
}

function Row({ step, data, vars, onChanged, index }) {
  const navigate = useNavigate();
  const S = STATUS[step.status];
  const isClass = step.kind === 'class';
  const prev = index > 0 ? data.steps[index - 1] : null;
  const waitsOnPrev = step.status === 'todo' && prev && prev.status !== 'complete';
  const act = async () => {
    if (step.done_when === 'visit') { try { await api.post('/api/next-steps/done', { key: step.key }); onChanged?.(); } catch (e) { /* ignore */ } }
    const u = String(step.button_url || '').trim();
    if (/^https?:\/\//i.test(u)) window.open(u, '_blank', 'noopener,noreferrer'); else navigate(u || '/dashboard');
  };
  return (
    <div className={`grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_150px_minmax(0,2fr)] gap-x-6 gap-y-2 px-4 md:px-5 py-4 border-t border-slate-100 ${step.status === 'current' ? 'bg-blue-50/40' : ''}`} data-testid={`ns-step-row-${step.key}`} data-status={step.status}>
      <div className="flex items-start gap-3 min-w-0">
        <span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5 ${step.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : step.status === 'current' ? 'bg-[#002FA7] text-white' : 'bg-slate-100 text-slate-500'}`}>{index + 1}</span>
        <div className="min-w-0">
          <p className={`text-sm font-semibold leading-snug ${step.status === 'complete' ? 'text-slate-500 line-through decoration-slate-300' : 'text-slate-900'}`} data-testid={`ns-step-title-${step.key}`}>{fill(step.title, vars)}</p>
          {isClass && step.status !== 'complete' && <ClassMeta cls={data.class} />}
        </div>
      </div>
      <div className={`inline-flex items-center gap-1.5 text-sm font-medium ${S.cls}`} data-testid={`ns-step-status-${step.key}`}>
        <S.Icon size={16} /> {S.label}
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-600 leading-relaxed flex-1 min-w-[160px]" data-testid={`ns-step-desc-${step.key}`}>{fill(step.description, vars)}</p>
        {step.status === 'current' && (
          <div className="shrink-0" data-testid={`ns-step-action-${step.key}`}>
            {isClass ? <ClassAction cls={data.class} texts={data.texts} onChanged={onChanged} /> : (
              <button type="button" onClick={act} className="inline-flex items-center gap-1.5 rounded-full bg-[#002FA7] hover:bg-[#00248a] text-white px-4 py-2 text-xs font-bold transition" data-testid="ns-row-cta">{fill(step.button_label, vars)} <ArrowRight size={13} /></button>
            )}
          </div>
        )}
        {waitsOnPrev && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 shrink-0 mt-1"><Lock size={11} /> after step {index}</span>}
      </div>
    </div>
  );
}

/** Microsoft-admin-centre style checklist: progress bar + Action · Status · Description rows. */
export default function StepsChecklist({ data, onChanged }) {
  const vars = stepVars(data);
  const { done, total } = data.progress;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <section className="bg-white rounded-[28px] border border-slate-200/80 shadow-sm overflow-hidden" data-testid="ns-checklist">
      <div className="px-4 md:px-5 pt-5 pb-4">
        <h2 className="font-[Manrope] text-lg font-extrabold text-slate-900 tracking-tight" data-testid="ns-checklist-title">{fill(data.texts.start_here.title, vars)}</h2>
        <p className="text-sm text-slate-500 mt-0.5" data-testid="ns-checklist-intro">{fill(data.texts.start_here.intro, vars)}</p>
        <div className="mt-4 max-w-md">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1.5">
            <span>{data.texts.start_here.progress_label}</span>
            <span data-testid="ns-progress">{done} / {total}</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden" role="progressbar" aria-valuenow={done} aria-valuemax={total}>
            <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} data-testid="ns-progress-bar" />
          </div>
        </div>
      </div>
      <div className="hidden md:grid grid-cols-[minmax(0,1.5fr)_150px_minmax(0,2fr)] gap-x-6 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-t border-slate-100 bg-slate-50/60">
        <span>Action</span><span>Status</span><span>Description</span>
      </div>
      {data.steps.map((s, i) => <Row key={s.key} step={s} index={i} data={data} vars={vars} onChanged={onChanged} />)}
    </section>
  );
}
